import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  CartesianAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useTranslation } from "react-i18next";

const sample = {
  year: [
    {
      date: "2012",
      magic: 24.6,
      low: 42.1,
      growth: 3.8,
      quant: 52.4,
      cospi: 9.4,
      kosdaq: -0.8,
    },
    {
      date: "2013",
      magic: 24.6,
      low: 42.1,
      growth: 3.8,
      quant: 52.4,
      cospi: 9.4,
      kosdaq: -0.8,
    },
    {
      date: "2014",
      magic: 24.6,
      low: 42.1,
      growth: 3.8,
      quant: 52.4,
      cospi: 9.4,
      kosdaq: -0.8,
    },
  ],
  accum: [
    {
      date: "2012",
      magic: 24.6,
      low: 42.1,
      growth: 3.8,
      quant: 52.4,
      cospi: 9.4,
      kosdaq: -0.8,
    },
    {
      date: "2013",
      magic: 24.6,
      low: 42.1,
      growth: 3.8,
      quant: 52.4,
      cospi: 9.4,
      kosdaq: -0.8,
    },
    {
      date: "2014",
      magic: 24.6,
      low: 42.1,
      growth: 3.8,
      quant: 52.4,
      cospi: 9.4,
      kosdaq: -0.8,
    },
  ],
  month: [
    {
      date: "2012-01",
      magic: 24.6,
      low: 42.1,
      growth: 3.8,
      quant: 52.4,
      cospi: 9.4,
      kosdaq: -0.8,
    },
    {
      date: "2012-02",
      magic: 24.6,
      low: 42.1,
      growth: 3.8,
      quant: 52.4,
      cospi: 9.4,
      kosdaq: -0.8,
    },
    {
      date: "2012-03",
      magic: 24.6,
      low: 42.1,
      growth: 3.8,
      quant: 52.4,
      cospi: 9.4,
      kosdaq: -0.8,
    },
  ],
};

export const HomeBanner = (props) => {
  const { t } = useTranslation(); // useTranslation 훅을 사용하여 t 함수를 가져옴
  const history = useHistory();
  const {
    chartType,
    chartData,
    onChangeChartType,
    regionList = [],
    selectedRegion,
    onChangeRegion,
  } = props;

  // 범례 클릭으로 숨긴 시리즈 키 목록
  const [hiddenKeys, setHiddenKeys] = useState([]);
  const toggleSeries = (key) => {
    setHiddenKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // 시리즈 키 -> 한글 라벨 (translations의 home.chart.label.<key>)
  const getSeriesLabel = (key) =>
    t("home.chart.label." + key, { defaultValue: key });

  const CHART_TYPE_LIST = [
    {
      value: "MONTH",
      label: t("home.chart.type.month"),
    },
    {
      value: "YEAR",
      label: t("home.chart.type.year"),
    },
    {
      value: "ACCUMULATE",
      label: t("home.chart.type.accumulate"),
    },
  ];

  // 차트 시리즈 정의 (그리는 순서대로). 데이터에 실제로 존재하는 키만 렌더링됨
  // 지수(dashed): 무채색 슬레이트 계열로 배경처럼 물러나게 (국가별 메인지수=진한톤 / 보조지수=연한톤)
  // 전략(solid): 선명한 색으로 도드라지게. 같은 전략의 소형/대형주포함은 같은 색 계열의 명도 차이로 묶음
  const SERIES_LIST = [
    // 지수 (벤치마크) - 점선 + 슬레이트 계열
    { key: "cospi", color: "#546E7A", dashed: true },
    { key: "kosdaq", color: "#B0BEC5", dashed: true },
    { key: "nasdaq", color: "#546E7A", dashed: true },
    { key: "sp500", color: "#B0BEC5", dashed: true },
    { key: "hangseng", color: "#546E7A", dashed: true },
    { key: "hscei", color: "#B0BEC5", dashed: true },
    { key: "topix", color: "#546E7A", dashed: true },
    { key: "nikkei225", color: "#B0BEC5", dashed: true },
    { key: "jkt", color: "#546E7A", dashed: true },
    { key: "lq45", color: "#B0BEC5", dashed: true },
    { key: "hochiminh", color: "#546E7A", dashed: true },
    { key: "hanoi", color: "#B0BEC5", dashed: true },
    // 전략 - 실선 + 선명한 색 (계열별: 마법=파랑 / 저벨류=앰버 / 고속성장=레드핑크 / 슈퍼퀀트=그린)
    { key: "magic", color: "#1565C0" }, // 마법 - 진한 파랑
    { key: "magicLarge", color: "#29B6F6" }, // 마법(대형) - 밝은 파랑
    { key: "low", color: "#F9A825" }, // 저벨류 - 앰버
    { key: "lowLarge", color: "#FDD835" }, // 저벨류(대형) - 밝은 옐로우
    { key: "growth", color: "#C62828" }, // 고속성장 - 진한 레드
    { key: "growthLarge", color: "#EC407A" }, // 고속성장(대형) - 핑크
    { key: "quant", color: "#2E7D32" }, // 슈퍼퀀트 - 진한 그린
    { key: "quantLarge", color: "#66BB6A" }, // 슈퍼퀀트(대형) - 밝은 그린
    { key: "trendQuant", color: "#7B1FA2" }, // 트렌드퀀트 - 퍼플
    { key: "trendQuantConc", color: "#7B1FA2" }, // 트렌드퀀트 - 퍼플
  ];

  // 현재 chartData에 값이 존재하는 키만 추출 (date 제외)
  const presentKeys = new Set();
  (chartData || []).forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (key !== "date" && row[key] !== null && row[key] !== undefined) {
        presentKeys.add(key);
      }
    });
  });
  const visibleSeries = SERIES_LIST.filter((item) => presentKeys.has(item.key));

  const buildContainerClassname = () => {
    let classname = "home-banner";

    return classname;
  };

  const renderCustomTick = (props) => {
    const { x, y, payload } = props;
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={24} textAnchor="middle" fill="#999999">
          {/* 라벨 값과 차트 값 사이의 간격 설정 */}
          <tspan>{payload.value}</tspan>
          <tspan x={0} dy={24} fontSize="12" fill="#999999">
            {payload.amt}
          </tspan>
        </text>
      </g>
    );
  };

  return (
    <article className={buildContainerClassname()}>
      <div className="banner__wrapper">
        <section className="text__box">
          {/* <h2>왜 퀀트투자일까요?</h2> */}
          {/* <p>풍요로운 노후를 즐기고 싶으신가요?</p>
          <p>현재의 인생을 즐기고 싶으신가요?</p>
          <p>돈을 가장 효율적으로 불릴 수 있는 방법은 있을까요?</p> */}
          {/* <h1>
            돈을 가장 가성비 좋게 효율적으로
            <br /> 불릴 수 있는 방법이 있을까요?
          </h1>
          <p>
            퀀트는 숫자에 기반한 수학통계적 기법으로 투자하며, 사람의 판단이
            개입되지 않습니다. <br /> 보통 우리가 지금까지 주식투자로 실패했던
            이유는 바로 사람의 판단이 들어갔기 때문입니다. <br /> 수학통계적
            기법을 따르는 퀀트투자는 장기적으로 부자가 될 확률이 가장 높은
            투자법입니다. 퀀트킹과 함께 투자하면, 노후 준비는 물론이고, 현재의
            인생도 즐길 수 있습니다.
            <br /> 퀀트킹이 이상을 현실로 만드는 첫걸음을 안전하게 이끌어
            드립니다.
          </p> */}
          <h1>{t("home.chart.title")}</h1>
          <p>{t("home.chart.desc")}</p>
          <h4>{t("home.chart.ment")}</h4>
        </section>
        <section className="chart__box">
          <header>
            {regionList.length > 0 && (
              <ul className="region__tab">
                {regionList.map((item, index) => {
                  let className = "";
                  if (item.value === selectedRegion) className += " on";
                  if (item.disabled) className += " disabled";
                  return (
                    <li
                      onClick={() => {
                        if (item.disabled) return;
                        onChangeRegion(item.value);
                      }}
                      key={"home-banner-chart-region-li-" + index}
                      className={className.trim()}
                    >
                      <span>{item.label}</span>
                    </li>
                  );
                })}
              </ul>
            )}
            <ul className="type__tab">
              {CHART_TYPE_LIST.map((item, index) => {
                return (
                  <li
                    onClick={() => {
                      onChangeChartType(item.value);
                    }}
                    key={"home-banner-chart-type-li-" + index}
                    className={item.value === chartType ? "on" : ""}
                  >
                    <span>{item.label}</span>
                  </li>
                );
              })}
            </ul>
          </header>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{
                top: 20,
                right: 40,
                left: 10,
                bottom: 20,
              }}
            >
              <XAxis
                tick={renderCustomTick}
                padding={{ top: 0, bttom: 20 }}
                dataKey="date"
                stroke="#999999"
              />
              <YAxis unit="%" stroke="#999999" />
              <Tooltip
                formatter={(value, name) => {
                  return [value + "%", getSeriesLabel(name)];
                }}
              />
              <Legend
                verticalAlign="top"
                content={() => {
                  // 데이터에 존재하는 시리즈를 범례로 표시. 클릭하면 해당 선을 켜고 끔
                  return (
                    <ul className="banner-chart__legend">
                      {visibleSeries.map((item) => {
                        const hidden = hiddenKeys.includes(item.key);
                        return (
                          <li
                            key={item.key}
                            className={hidden ? "inactive" : ""}
                            onClick={() => toggleSeries(item.key)}
                          >
                            <div
                              style={{ backgroundColor: item.color }}
                              className="circle"
                            ></div>
                            <span style={{ color: item.color }}>
                              {getSeriesLabel(item.key)}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  );
                }}
              />
              {visibleSeries.map((item) => (
                <Line
                  key={item.key}
                  strokeWidth={item.dashed ? 1.5 : 2.5}
                  strokeDasharray={item.dashed ? "5 5" : undefined}
                  type="monotone"
                  dataKey={item.key}
                  stroke={item.color}
                  activeDot={{ r: 4 }}
                  dot={false}
                  hide={hiddenKeys.includes(item.key)}
                />
              ))}
              <CartesianGrid strokeDasharray="3 3" />
            </LineChart>
          </ResponsiveContainer>
        </section>
      </div>
    </article>
  );
};
